import { useState, useEffect } from 'react';
import { parseRoute, getTransitionDirection } from './navigationUtils';

// Custom hook for managing navigation stack
export const useNavigationStack = (appLocation, initAppLocation, animationDirectionsRef) => {
  // Initialize navigation stack with the initial route
  const initialPage = parseRoute(initAppLocation);
  const [navigationStack, setNavigationStack] = useState([{ ...initialPage, timestamp: Date.now() }]);
  const [chatId, setChatId] = useState(initialPage.chatId);
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousPageContext, setPreviousPageContext] = useState(null);

  useEffect(() => {
    const newPage = parseRoute(appLocation);
    if (newPage.chatId) {
      setChatId(newPage.chatId);
    }

    setNavigationStack(prev => {
      const lastPage = prev[prev.length - 1];
      
      // If this is the same page, don't change the stack
      if (lastPage && lastPage.type === newPage.type && 
          JSON.stringify(lastPage.data) === JSON.stringify(newPage.data)) {
        setIsGoingBack(false);
        setPreviousPageContext(null);
        return prev;
      }

      const backPage = prev[prev.length - 2];
      if (backPage && backPage.type === newPage.type && 
          JSON.stringify(backPage.data) === JSON.stringify(newPage.data)) {
        // If going back to the previous page, truncate stack to that point
        const isGoingBackValue = true;
        const animationDirection = getTransitionDirection(newPage, isGoingBackValue, lastPage);
        
        // Store animation direction in ref using page key
        animationDirectionsRef.current = animationDirection;
        
        setIsGoingBack(isGoingBackValue);
        setIsInitialLoad(false);
        setPreviousPageContext(lastPage);
        
        return prev.slice(0, prev.length - 1);
      }

      // Moving forward to a new page - add to stack
      const isGoingBackValue = false;
      const animationDirection = getTransitionDirection(newPage, isGoingBackValue, lastPage);
      const newPageWithTimestamp = { ...newPage, timestamp: Date.now() };
      
      // Store animation direction in ref using page key
      animationDirectionsRef.current = animationDirection;
      
      setIsGoingBack(isGoingBackValue);
      setIsInitialLoad(false);
      setPreviousPageContext(lastPage);
      
      return [...prev, newPageWithTimestamp];
    });

    window.scrollTo(0, 0);
  }, [appLocation]);

  return {
    navigationStack,
    chatId,
    isGoingBack,
    isInitialLoad,
    previousPageContext
  };
};